import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from "@/components/ui/command";
import { ShieldAlert, Activity, Inbox, LineChart, Camera, Map as MapIcon, Zap, FlameKindling, Users, AlertTriangle, Car } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";

export const CommandPalette = ({ open, setOpen }) => {
  const nav = useNavigate();
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  const go = (path) => { setOpen(false); nav(path); };

  const inject = async (t) => {
    setOpen(false);
    try {
      const ev = await api.inject(t);
      toast.error(`INJECTED ${ev.title}`, { description: ev.description });
    } catch { toast.error("Inject failed"); }
  };

  const runScenario = async (name) => {
    setOpen(false);
    try {
      await api.runScenario(name);
      toast(`Scenario "${name}" started`, { description: "Events will fire over the next 20 seconds." });
    } catch { toast.error("Scenario failed"); }
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search · navigate · inject events… (⌘K)" data-testid="cmdk-input" />
      <CommandList>
        <CommandEmpty>No commands matched.</CommandEmpty>

        <CommandGroup heading="Navigate">
          <CommandItem onSelect={() => go("/")} data-testid="cmdk-go-command"><ShieldAlert className="mr-2 h-4 w-4" /> Command Center</CommandItem>
          <CommandItem onSelect={() => go("/crowd")}><Activity className="mr-2 h-4 w-4" /> Crowd Intelligence</CommandItem>
          <CommandItem onSelect={() => go("/incidents")}><Inbox className="mr-2 h-4 w-4" /> Incidents</CommandItem>
          <CommandItem onSelect={() => go("/predictive")}><LineChart className="mr-2 h-4 w-4" /> Predictive Risk</CommandItem>
          <CommandItem onSelect={() => go("/cameras")}><Camera className="mr-2 h-4 w-4" /> Cameras</CommandItem>
          <CommandItem onSelect={() => go("/map")}><MapIcon className="mr-2 h-4 w-4" /> Stadium Map</CommandItem>
          <CommandItem onSelect={() => go("/parking")}><Car className="mr-2 h-4 w-4" /> Parking</CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Demo · Inject Event">
          <CommandItem onSelect={() => inject("crowd_surge")} data-testid="cmdk-inject-surge"><Zap className="mr-2 h-4 w-4 sev-critical" /> Crowd Surge</CommandItem>
          <CommandItem onSelect={() => inject("stampede_risk")}><AlertTriangle className="mr-2 h-4 w-4 sev-critical" /> Stampede Risk</CommandItem>
          <CommandItem onSelect={() => inject("fire_smoke")}><FlameKindling className="mr-2 h-4 w-4 sev-critical" /> Fire / Smoke</CommandItem>
          <CommandItem onSelect={() => inject("fight")}><Users className="mr-2 h-4 w-4 sev-high" /> Altercation</CommandItem>
          <CommandItem onSelect={() => inject("fall")}>Person Collapsed</CommandItem>
          <CommandItem onSelect={() => inject("abandoned_bag")}>Unattended Bag</CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Demo · Run Scenario">
          <CommandItem onSelect={() => runScenario("match_day_critical")} data-testid="cmdk-scenario-critical">
            Match-Day Critical Cascade
            <CommandShortcut>8 steps</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => runScenario("vip_escort")}>
            VIP Escort Threat
            <CommandShortcut>4 steps</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => runScenario("egress")}>
            Post-Match Egress
            <CommandShortcut>5 steps</CommandShortcut>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Preferences">
          <CommandItem onSelect={() => { toggle(); setOpen(false); }} data-testid="cmdk-toggle-theme">
            Switch to {theme === "dark" ? "Light" : "Dark"} theme
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
